// Normalized view models for the five approval-gated v3 communications
// tools. `model/comms_presentation.ts` builds these from tool entries;
// unknown tools and malformed payloads never produce one. Known communications
// tools fail closed when malformed so the coordinator cannot approve a send
// without seeing the recipient and message.

export const V3_COMMS_TOOL_NAMES = [
    'send_sms',
    'place_voice_call',
    'approve_pending_message',
    'edit_pending_message',
    'reject_pending_message',
] as const;

export type V3CommsToolName = (typeof V3_COMMS_TOOL_NAMES)[number];

const V3_COMMS_TOOL_NAME_SET: ReadonlySet<string> = new Set(
    V3_COMMS_TOOL_NAMES,
);

export function isV3CommsToolName(value: string): value is V3CommsToolName {
    return V3_COMMS_TOOL_NAME_SET.has(value);
}

export type V3CommsRecipient = {
    readonly name: string;
    readonly content: string;
};

export type V3CommsProposal =
    | {
          readonly tool: 'send_sms';
          readonly recipients: ReadonlyArray<V3CommsRecipient>;
          // Set when every recipient gets identical wording; the card then
          // shows one bubble instead of one per recipient.
          readonly sharedContent: string | null;
          readonly targetLanguage: string | null;
          readonly sendAnyway: boolean;
      }
    | {
          readonly tool: 'place_voice_call';
          readonly name: string;
          readonly instructions: string;
          readonly openingLine: string | null;
          readonly sendAnyway: boolean;
      }
    | {
          readonly tool: 'approve_pending_message';
          readonly name: string;
          readonly content: string;
          readonly isRewrite: boolean;
      }
    | {
          readonly tool: 'edit_pending_message';
          readonly content: string;
      }
    | {
          readonly tool: 'reject_pending_message';
          readonly reason: string | null;
      };

export type V3CommsChipTone =
    | 'success'
    | 'info'
    | 'warning'
    | 'destructive'
    | 'muted';

export type V3CommsReceipt = {
    readonly title: string;
    readonly detail: string | null;
    readonly chip: {
        readonly label: string;
        readonly tone: V3CommsChipTone;
    };
};

type V3CommsPresentationBase = {
    readonly kind: 'comms';
    readonly proposal: V3CommsProposal;
};

export type V3CommsPresentation = V3CommsPresentationBase &
    (
        | {
              readonly phase: 'needs_review' | 'working';
              readonly receipt: null;
          }
        | {
              readonly phase: 'settled';
              readonly receipt: V3CommsReceipt;
          }
    );

export function smsAudience(
    recipients: ReadonlyArray<V3CommsRecipient>,
): string {
    return recipients.length === 1 && recipients[0] !== undefined
        ? recipients[0].name
        : `${recipients.length} recipients`;
}

export function optionalTrimmed(
    value: string | null | undefined,
): string | null {
    const trimmed = value?.trim() ?? '';
    return trimmed.length > 0 ? trimmed : null;
}
